import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { commesseAPI, ruoliAPI, risorseAPI } from '../lib/api';
import { formatCurrency, formatPercent, formatDate, getStatusLabel } from '../lib/utils';
import { StatusBadge } from '../components/common/StatusBadge';
import { KPICard } from '../components/common/KPICard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft,
  Euro,
  TrendingUp,
  Clock,
  Target,
  Plus,
  Trash2,
  Users,
  Layers,
  Wallet,
  Plane,
  Package,
  Loader2,
  Calendar,
  Lock,
  Unlock
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const categoriaEsterno = [
  { value: 'consulenza', label: 'Consulenza' },
  { value: 'fornitore', label: 'Fornitore' },
  { value: 'software', label: 'Software' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'altro', label: 'Altro' }
];

const categoriaTrasferta = [
  { value: 'viaggio', label: 'Viaggio' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'pasti', label: 'Pasti' },
  { value: 'altro', label: 'Altro' }
];

export const CommessaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [commessa, setCommessa] = useState(null);
  const [ruoli, setRuoli] = useState([]);
  const [risorse, setRisorse] = useState([]);
  const [team, setTeam] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosingDialogOpen, setIsClosingDialogOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  
  // Dialog states
  const [budgetRisorsaDialog, setBudgetRisorsaDialog] = useState(false);
  const [budgetEsternoDialog, setBudgetEsternoDialog] = useState(false);
  const [budgetTrasfertaDialog, setBudgetTrasfertaDialog] = useState(false);
  const [faseDialog, setFaseDialog] = useState(false);
  const [teamDialog, setTeamDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [budgetRisorsaForm, setBudgetRisorsaForm] = useState({
    ruolo_id: '', risorsa_id: '', ore_previste: 0, costo_orario: 0, ricavo_orario: 0
  });
  const [budgetEsternoForm, setBudgetEsternoForm] = useState({
    categoria: 'consulenza', descrizione: '', fornitore: '', costo_previsto: 0, data_prevista: ''
  });
  const [budgetTrasfertaForm, setBudgetTrasfertaForm] = useState({
    descrizione: '', persona: '', categoria: 'viaggio', costo_previsto: 0
  });
  const [faseForm, setFaseForm] = useState({ nome: '', descrizione: '' });
  const [selectedRisorsaId, setSelectedRisorsaId] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [commessaRes, ruoliRes, risorseRes] = await Promise.all([
        commesseAPI.getById(id),
        ruoliAPI.getAll(),
        risorseAPI.getAll()
      ]);
      setCommessa(commessaRes.data);
      setRuoli(ruoliRes.data);
      setRisorse(risorseRes.data);
      
      // Load team
      const teamRes = await commesseAPI.getTeam(id);
      setTeam(teamRes.data);
    } catch (error) {
      console.error('Error loading commessa:', error);
      toast.error('Errore nel caricamento della commessa');
    } finally {
      setIsLoading(false);
    }
  };

  // Chiusura/Riapertura commessa
  const canCloseCommessa = user?.ruolo === 'admin' || user?.ruolo === 'coordinator';
  
  const handleChiudiCommessa = async () => {
    setIsClosing(true);
    try {
      await commesseAPI.chiudi(id);
      toast.success('Commessa chiusa con successo');
      loadData();
    } catch (error) {
      console.error('Error closing commessa:', error);
      toast.error(error.response?.data?.detail || 'Errore nella chiusura della commessa');
    } finally {
      setIsClosing(false);
      setIsClosingDialogOpen(false);
    }
  };

  const handleRiapriCommessa = async () => {
    setIsClosing(true);
    try {
      await commesseAPI.riapri(id);
      toast.success('Commessa riaperta con successo');
      loadData();
    } catch (error) {
      console.error('Error reopening commessa:', error);
      toast.error(error.response?.data?.detail || 'Errore nella riapertura della commessa');
    } finally {
      setIsClosing(false);
      setIsClosingDialogOpen(false);
    }
  };

  // Budget Risorsa handlers
  const handleAddBudgetRisorsa = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await commesseAPI.addBudgetRisorsa(id, {
        ...budgetRisorsaForm,
        ore_previste: parseFloat(budgetRisorsaForm.ore_previste),
        costo_orario: parseFloat(budgetRisorsaForm.costo_orario),
        ricavo_orario: parseFloat(budgetRisorsaForm.ricavo_orario),
        risorsa_id: budgetRisorsaForm.risorsa_id || null
      });
      toast.success('Budget risorsa aggiunto');
      setBudgetRisorsaDialog(false);
      setBudgetRisorsaForm({ ruolo_id: '', risorsa_id: '', ore_previste: 0, costo_orario: 0, ricavo_orario: 0 });
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiunta del budget');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBudgetRisorsa = async (budgetId) => {
    try {
      await commesseAPI.deleteBudgetRisorsa(id, budgetId);
      toast.success('Budget eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Budget Esterno handlers
  const handleAddBudgetEsterno = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await commesseAPI.addBudgetEsterno(id, {
        ...budgetEsternoForm,
        costo_previsto: parseFloat(budgetEsternoForm.costo_previsto)
      });
      toast.success('Costo esterno aggiunto');
      setBudgetEsternoDialog(false);
      setBudgetEsternoForm({ categoria: 'consulenza', descrizione: '', fornitore: '', costo_previsto: 0, data_prevista: '' });
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiunta del costo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBudgetEsterno = async (budgetId) => {
    try {
      await commesseAPI.deleteBudgetEsterno(id, budgetId);
      toast.success('Costo eliminato');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Budget Trasferta handlers
  const handleAddBudgetTrasferta = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await commesseAPI.addBudgetTrasferta(id, {
        ...budgetTrasfertaForm,
        costo_previsto: parseFloat(budgetTrasfertaForm.costo_previsto)
      });
      toast.success('Trasferta aggiunta');
      setBudgetTrasfertaDialog(false);
      setBudgetTrasfertaForm({ descrizione: '', persona: '', categoria: 'viaggio', costo_previsto: 0 });
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiunta della trasferta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBudgetTrasferta = async (budgetId) => {
    try {
      await commesseAPI.deleteBudgetTrasferta(id, budgetId);
      toast.success('Trasferta eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Fase handlers
  const handleAddFase = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await commesseAPI.addFase(id, faseForm);
      toast.success('Fase aggiunta');
      setFaseDialog(false);
      setFaseForm({ nome: '', descrizione: '' });
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiunta della fase');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFase = async (faseId) => {
    try {
      await commesseAPI.deleteFase(id, faseId);
      toast.success('Fase eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Team handlers
  const handleAddTeamMember = async () => {
    if (!selectedRisorsaId) return;
    try {
      await commesseAPI.addTeamMember(id, selectedRisorsaId);
      toast.success('Risorsa aggiunta al team');
      setTeamDialog(false);
      setSelectedRisorsaId('');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'aggiunta della risorsa');
    }
  };

  const handleRemoveTeamMember = async (risorsaId) => {
    try {
      await commesseAPI.removeTeamMember(id, risorsaId);
      toast.success('Risorsa rimossa dal team');
      loadData();
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  // Get ruolo name
  const getRuoloName = (ruoloId) => {
    const ruolo = ruoli.find(r => r.id === ruoloId);
    return ruolo?.nome || '-';
  };

  // Handle ruolo selection for budget
  const handleRuoloChange = (ruoloId) => {
    const ruolo = ruoli.find(r => r.id === ruoloId);
    setBudgetRisorsaForm({
      ...budgetRisorsaForm,
      ruolo_id: ruoloId,
      costo_orario: ruolo?.costo_standard || 0,
      ricavo_orario: ruolo?.tariffa_standard || 0
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="commessa-detail-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!commessa) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Commessa non trovata</p>
        <Button variant="outline" onClick={() => navigate('/commesse')} className="mt-4">
          Torna alle Commesse
        </Button>
      </div>
    );
  }

  const kpi = commessa.kpi || {};

  return (
    <div className="space-y-6" data-testid="commessa-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/commesse')} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-sm text-muted-foreground">{commessa.codice_commessa}</span>
              <StatusBadge status={commessa.tipo_commessa} />
              <StatusBadge status={commessa.stato} />
            </div>
            <h1 className="text-2xl font-bold">{commessa.nome_progetto}</h1>
            {commessa.cliente_nome && (
              <p className="text-muted-foreground">{commessa.cliente_nome}</p>
            )}
          </div>
        </div>
        
        {/* Chiudi/Riapri Button */}
        {canCloseCommessa && (
          <div className="flex gap-2">
            {commessa.stato === 'chiusa' ? (
              <Button 
                variant="outline"
                onClick={() => setIsClosingDialogOpen(true)}
                data-testid="riapri-commessa-btn"
              >
                <Unlock className="h-4 w-4 mr-2" />
                Riapri Commessa
              </Button>
            ) : (
              <Button 
                variant="outline"
                onClick={() => setIsClosingDialogOpen(true)}
                data-testid="chiudi-commessa-btn"
              >
                <Lock className="h-4 w-4 mr-2" />
                Chiudi Commessa
              </Button>
            )}
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KPICard
          title="Contratto"
          value={formatCurrency(commessa.budget_contratto)}
          icon={Euro}
          data-testid="kpi-contratto"
        />
        <KPICard
          title="Ore Previste"
          value={`${kpi.ore_totali_previste || 0}h`}
          subtitle={`Lavorate: ${kpi.ore_consuntive || 0}h`}
          icon={Clock}
          data-testid="kpi-ore-previste"
        />
        <KPICard
          title="Costi Consuntivi"
          value={formatCurrency(kpi.costi_totali_consuntivo || 0)}
          subtitle={`Budget: ${formatCurrency(kpi.budget_totale || 0)}`}
          icon={Wallet}
          valueClassName={(kpi.costi_totali_consuntivo || 0) <= (kpi.budget_totale || 0) ? 'text-emerald-600' : 'text-red-600'}
          data-testid="kpi-costi"
        />
        <KPICard
          title="Marginalità Budget"
          value={formatPercent(commessa.budget_contratto > 0 ? ((commessa.budget_contratto - (kpi.budget_totale || 0)) / commessa.budget_contratto * 100) : 0)}
          subtitle={formatCurrency(commessa.budget_contratto - (kpi.budget_totale || 0))}
          icon={TrendingUp}
          valueClassName={(() => {
            const margine = commessa.budget_contratto > 0 
              ? ((commessa.budget_contratto - (kpi.budget_totale || 0)) / commessa.budget_contratto * 100) 
              : 0;
            return margine >= 55 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
          })()}
          data-testid="kpi-marginalita-budget"
        />
      </div>
      
      {/* KPI Cards Row 2 - Trasferte, Costi Esterni, Avanzamento, Marginalità Effettiva */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Trasferte"
          value={formatCurrency(kpi.trasferte_consuntivo || 0)}
          subtitle={`Budget: ${formatCurrency(kpi.budget_totale_trasferte || 0)}`}
          icon={Plane}
          valueClassName={(() => {
            const budget = kpi.budget_totale_trasferte || 0;
            const consuntivo = kpi.trasferte_consuntivo || 0;
            return budget > 0 && consuntivo <= budget ? 'text-emerald-600' : (budget > 0 ? 'text-red-600' : '');
          })()}
          data-testid="kpi-trasferte"
        >
          {(kpi.budget_totale_trasferte || 0) > 0 && (
            <div className="mt-2">
              <Progress 
                value={Math.min(100, kpi.avanzamento_trasferte || 0)} 
                className="h-1.5"
              />
              <p className="text-xs mt-1 text-muted-foreground">
                Avanzamento: {kpi.avanzamento_trasferte || 0}%
              </p>
            </div>
          )}
        </KPICard>
        <KPICard
          title="Costi Esterni"
          value={formatCurrency(kpi.costi_esterni_consuntivo || 0)}
          subtitle={`Budget: ${formatCurrency(kpi.budget_totale_esterni || 0)}`}
          icon={Package}
          valueClassName={(() => {
            const budget = kpi.budget_totale_esterni || 0;
            const consuntivo = kpi.costi_esterni_consuntivo || 0;
            return budget > 0 && consuntivo <= budget ? 'text-emerald-600' : (budget > 0 ? 'text-red-600' : '');
          })()}
          data-testid="kpi-costi-esterni"
        >
          {(kpi.budget_totale_esterni || 0) > 0 && (
            <div className="mt-2">
              <Progress 
                value={Math.min(100, kpi.avanzamento_costi_esterni || 0)} 
                className="h-1.5"
              />
              <p className="text-xs mt-1 text-muted-foreground">
                Avanzamento: {kpi.avanzamento_costi_esterni || 0}%
              </p>
            </div>
          )}
        </KPICard>
        <KPICard
          title="Avanzamento Ore"
          value={`${kpi.avanzamento_ore || 0}%`}
          subtitle={`${kpi.ore_consuntive || 0}h / ${kpi.ore_totali_previste || 0}h`}
          icon={Target}
          valueClassName={kpi.avanzamento_ore <= 100 ? 'text-emerald-600' : 'text-red-600'}
          data-testid="kpi-avanzamento"
        />
        {/* Marginalità Effettiva - Contratto vs Costi Totali Consuntivi */}
        <KPICard
          title="Marginalità Effettiva"
          value={formatPercent(commessa.budget_contratto > 0 ? ((commessa.budget_contratto - (kpi.costi_totali_consuntivo || 0)) / commessa.budget_contratto * 100) : 0)}
          subtitle={formatCurrency(commessa.budget_contratto - (kpi.costi_totali_consuntivo || 0))}
          icon={TrendingUp}
          valueClassName={(() => {
            const margineEffettivo = commessa.budget_contratto > 0 
              ? ((commessa.budget_contratto - (kpi.costi_totali_consuntivo || 0)) / commessa.budget_contratto * 100) 
              : 0;
            return margineEffettivo >= 55 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
          })()}
          data-testid="kpi-marginalita-effettiva"
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="budget" data-testid="tab-budget">Budget</TabsTrigger>
          <TabsTrigger value="wbs" data-testid="tab-wbs">Fasi (WBS)</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>Informazioni Generali</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Data Inizio</p>
                    <p className="font-medium">{formatDate(commessa.data_inizio)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Data Fine Prevista</p>
                    <p className="font-medium">{formatDate(commessa.data_fine_prevista)}</p>
                  </div>
                </div>
                {commessa.pm_nome && (
                  <div>
                    <p className="text-sm text-muted-foreground">Project Manager</p>
                    <p className="font-medium">{commessa.pm_nome}</p>
                  </div>
                )}
                {commessa.descrizione && (
                  <div>
                    <p className="text-sm text-muted-foreground">Descrizione</p>
                    <p className="text-sm">{commessa.descrizione}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Budget Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle>Budget vs Consuntivo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Ore */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium">Ore</span>
                    <span className="text-sm font-mono">
                      {kpi.ore_consuntive || 0}h / {kpi.ore_totali_previste || 0}h
                    </span>
                  </div>
                  <Progress 
                    value={kpi.ore_totali_previste > 0 ? Math.min(100, (kpi.ore_consuntive / kpi.ore_totali_previste) * 100) : 0} 
                    className="h-2"
                  />
                  <p className={`text-xs mt-1 ${kpi.delta_ore >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {kpi.delta_ore >= 0 ? `${kpi.delta_ore}h rimanenti` : `${Math.abs(kpi.delta_ore)}h in eccesso`}
                  </p>
                </div>
                
                {/* Costi Risorse */}
                <div className="pt-3 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Costo Risorse (Budget)</span>
                    <span className="font-mono">{formatCurrency(kpi.budget_totale_risorse || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Costo Risorse (Consuntivo)</span>
                    <span className={`font-mono font-semibold ${kpi.costo_consuntivo_risorse <= kpi.budget_totale_risorse ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(kpi.costo_consuntivo_risorse || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="font-medium">Delta</span>
                    <span className={`font-mono font-semibold ${kpi.delta_costo >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {kpi.delta_costo >= 0 ? '+' : ''}{formatCurrency(kpi.delta_costo || 0)}
                    </span>
                  </div>
                </div>

                {/* Altri Costi */}
                <div className="pt-3 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Costi Esterni (Budget)</span>
                    <span className="font-mono">{formatCurrency(kpi.budget_totale_esterni || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Costi Esterni (Consuntivo)</span>
                    <span className={`font-mono font-semibold ${(kpi.costi_esterni_consuntivo || 0) <= (kpi.budget_totale_esterni || 0) ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(kpi.costi_esterni_consuntivo || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-muted-foreground">Trasferte (Budget)</span>
                    <span className="font-mono">{formatCurrency(kpi.budget_totale_trasferte || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Trasferte (Consuntivo)</span>
                    <span className={`font-mono font-semibold ${(kpi.trasferte_consuntivo || 0) <= (kpi.budget_totale_trasferte || 0) ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(kpi.trasferte_consuntivo || 0)}
                    </span>
                  </div>
                </div>

                {/* Totale */}
                <div className="pt-3 border-t">
                  <div className="flex justify-between font-semibold">
                    <span>Budget Totale Costi</span>
                    <span className="font-mono">{formatCurrency(kpi.budget_totale || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-muted-foreground">Costi Totali Consuntivo</span>
                    <span className={`font-mono font-semibold ${
                      (kpi.costi_totali_consuntivo || 0) <= (kpi.budget_totale || 0)
                        ? 'text-emerald-600' 
                        : 'text-red-600'
                    }`}>
                      {formatCurrency(kpi.costi_totali_consuntivo || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-muted-foreground">Marginalità Attuale</span>
                    <span className={`font-mono font-semibold ${
                      commessa.budget_contratto > 0 && (commessa.budget_contratto - (kpi.costi_totali_consuntivo || 0)) >= 0 
                        ? 'text-emerald-600' 
                        : 'text-red-600'
                    }`}>
                      {formatCurrency(commessa.budget_contratto - (kpi.costi_totali_consuntivo || 0))} 
                      ({formatPercent(commessa.budget_contratto > 0 ? ((commessa.budget_contratto - (kpi.costi_totali_consuntivo || 0)) / commessa.budget_contratto * 100) : 0)})
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Budget Tab */}
        <TabsContent value="budget" className="space-y-6">
          {/* Budget Risorse */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Budget Risorse Interne
                </CardTitle>
                <CardDescription>Ore e costi previsti per ruolo</CardDescription>
              </div>
              <Button size="sm" onClick={() => setBudgetRisorsaDialog(true)} data-testid="add-budget-risorsa-btn">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ruolo</TableHead>
                    <TableHead className="text-right">Ore</TableHead>
                    <TableHead className="text-right">Costo/h</TableHead>
                    <TableHead className="text-right">Ricavo/h</TableHead>
                    <TableHead className="text-right">Costo Tot.</TableHead>
                    <TableHead className="text-right">Ricavo Tot.</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(commessa.budget_risorse || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nessun budget risorsa definito
                      </TableCell>
                    </TableRow>
                  ) : (
                    (commessa.budget_risorse || []).map((budget) => (
                      <TableRow key={budget.id}>
                        <TableCell>{getRuoloName(budget.ruolo_id)}</TableCell>
                        <TableCell className="text-right font-mono">{budget.ore_previste}h</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(budget.costo_orario)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(budget.ricavo_orario)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(budget.costo_totale)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(budget.ricavo_totale)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteBudgetRisorsa(budget.id)}
                            data-testid={`delete-budget-risorsa-${budget.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Budget Costi Esterni */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Costi Esterni
                </CardTitle>
                <CardDescription>Consulenti, fornitori e altri costi</CardDescription>
              </div>
              <Button size="sm" onClick={() => setBudgetEsternoDialog(true)} data-testid="add-budget-esterno-btn">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Fornitore</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(commessa.budget_costi_esterni || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nessun costo esterno definito
                      </TableCell>
                    </TableRow>
                  ) : (
                    (commessa.budget_costi_esterni || []).map((budget) => (
                      <TableRow key={budget.id}>
                        <TableCell>
                          <StatusBadge status={budget.categoria} showLabel={false}>
                            {categoriaEsterno.find(c => c.value === budget.categoria)?.label || budget.categoria}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>{budget.descrizione}</TableCell>
                        <TableCell>{budget.fornitore || '-'}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(budget.costo_previsto)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteBudgetEsterno(budget.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Budget Trasferte */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Plane className="h-5 w-5" />
                  Trasferte
                </CardTitle>
                <CardDescription>Viaggi, hotel e spese correlate</CardDescription>
              </div>
              <Button size="sm" onClick={() => setBudgetTrasfertaDialog(true)} data-testid="add-budget-trasferta-btn">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Persona</TableHead>
                    <TableHead className="text-right">Costo</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(commessa.budget_trasferte || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        Nessuna trasferta definita
                      </TableCell>
                    </TableRow>
                  ) : (
                    (commessa.budget_trasferte || []).map((budget) => (
                      <TableRow key={budget.id}>
                        <TableCell>
                          {categoriaTrasferta.find(c => c.value === budget.categoria)?.label || budget.categoria}
                        </TableCell>
                        <TableCell>{budget.descrizione}</TableCell>
                        <TableCell>{budget.persona || '-'}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(budget.costo_previsto)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteBudgetTrasferta(budget.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WBS Tab */}
        <TabsContent value="wbs" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5" />
                  Fasi del Progetto
                </CardTitle>
                <CardDescription>Struttura WBS della commessa</CardDescription>
              </div>
              <Button size="sm" onClick={() => setFaseDialog(true)} data-testid="add-fase-btn">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Fase
              </Button>
            </CardHeader>
            <CardContent>
              {(commessa.fasi || []).length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nessuna fase definita</p>
              ) : (
                <div className="space-y-3">
                  {(commessa.fasi || []).map((fase, index) => (
                    <div 
                      key={fase.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-mono text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-medium">{fase.nome}</h4>
                          {fase.descrizione && (
                            <p className="text-sm text-muted-foreground">{fase.descrizione}</p>
                          )}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeleteFase(fase.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team di Progetto
                </CardTitle>
                <CardDescription>Risorse assegnate alla commessa</CardDescription>
              </div>
              <Button size="sm" onClick={() => setTeamDialog(true)} data-testid="add-team-btn">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi Risorsa
              </Button>
            </CardHeader>
            <CardContent>
              {team.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nessuna risorsa assegnata</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {team.map((risorsa) => (
                    <div 
                      key={risorsa.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold">
                          {risorsa.nome?.charAt(0)}{risorsa.cognome?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium">{risorsa.nome} {risorsa.cognome}</p>
                          <p className="text-sm text-muted-foreground">{risorsa.email}</p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleRemoveTeamMember(risorsa.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Budget Risorsa Dialog */}
      <Dialog open={budgetRisorsaDialog} onOpenChange={setBudgetRisorsaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Budget Risorsa</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBudgetRisorsa} className="space-y-4">
            <div className="space-y-2">
              <Label>Ruolo *</Label>
              <Select value={budgetRisorsaForm.ruolo_id} onValueChange={handleRuoloChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  {ruoli.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Ore Previste</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={budgetRisorsaForm.ore_previste}
                  onChange={(e) => setBudgetRisorsaForm({...budgetRisorsaForm, ore_previste: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Costo/h (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetRisorsaForm.costo_orario}
                  onChange={(e) => setBudgetRisorsaForm({...budgetRisorsaForm, costo_orario: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Ricavo/h (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetRisorsaForm.ricavo_orario}
                  onChange={(e) => setBudgetRisorsaForm({...budgetRisorsaForm, ricavo_orario: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBudgetRisorsaDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={isSubmitting || !budgetRisorsaForm.ruolo_id}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Budget Esterno Dialog */}
      <Dialog open={budgetEsternoDialog} onOpenChange={setBudgetEsternoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Costo Esterno</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBudgetEsterno} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select value={budgetEsternoForm.categoria} onValueChange={(v) => setBudgetEsternoForm({...budgetEsternoForm, categoria: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriaEsterno.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Costo Previsto (€) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetEsternoForm.costo_previsto}
                  onChange={(e) => setBudgetEsternoForm({...budgetEsternoForm, costo_previsto: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Input
                value={budgetEsternoForm.descrizione}
                onChange={(e) => setBudgetEsternoForm({...budgetEsternoForm, descrizione: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Fornitore</Label>
              <Input
                value={budgetEsternoForm.fornitore}
                onChange={(e) => setBudgetEsternoForm({...budgetEsternoForm, fornitore: e.target.value})}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBudgetEsternoDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Budget Trasferta Dialog */}
      <Dialog open={budgetTrasfertaDialog} onOpenChange={setBudgetTrasfertaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Trasferta</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBudgetTrasferta} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select value={budgetTrasfertaForm.categoria} onValueChange={(v) => setBudgetTrasfertaForm({...budgetTrasfertaForm, categoria: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriaTrasferta.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Costo Previsto (€) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={budgetTrasfertaForm.costo_previsto}
                  onChange={(e) => setBudgetTrasfertaForm({...budgetTrasfertaForm, costo_previsto: e.target.value})}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Input
                value={budgetTrasfertaForm.descrizione}
                onChange={(e) => setBudgetTrasfertaForm({...budgetTrasfertaForm, descrizione: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Persona</Label>
              <Input
                value={budgetTrasfertaForm.persona}
                onChange={(e) => setBudgetTrasfertaForm({...budgetTrasfertaForm, persona: e.target.value})}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBudgetTrasfertaDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fase Dialog */}
      <Dialog open={faseDialog} onOpenChange={setFaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Fase</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddFase} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Fase *</Label>
              <Input
                value={faseForm.nome}
                onChange={(e) => setFaseForm({...faseForm, nome: e.target.value})}
                placeholder="Es. Analisi, Design, Sviluppo..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Input
                value={faseForm.descrizione}
                onChange={(e) => setFaseForm({...faseForm, descrizione: e.target.value})}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFaseDialog(false)}>Annulla</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team Dialog */}
      <Dialog open={teamDialog} onOpenChange={setTeamDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Risorsa al Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Seleziona Risorsa</Label>
              <Select value={selectedRisorsaId} onValueChange={setSelectedRisorsaId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona una risorsa" />
                </SelectTrigger>
                <SelectContent>
                  {risorse
                    .filter(r => !team.find(t => t.id === r.id))
                    .map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.nome} {r.cognome}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTeamDialog(false)}>Annulla</Button>
              <Button onClick={handleAddTeamMember} disabled={!selectedRisorsaId}>Aggiungi</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close/Reopen Commessa Dialog */}
      <AlertDialog open={isClosingDialogOpen} onOpenChange={setIsClosingDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {commessa.stato === 'chiusa' ? 'Riapri Commessa' : 'Chiudi Commessa'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {commessa.stato === 'chiusa' 
                ? 'Sei sicuro di voler riaprire questa commessa? Sarà nuovamente modificabile.'
                : 'Sei sicuro di voler chiudere questa commessa? Non sarà più modificabile.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={commessa.stato === 'chiusa' ? handleRiapriCommessa : handleChiudiCommessa}
              disabled={isClosing}
            >
              {isClosing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {commessa.stato === 'chiusa' ? 'Riapri' : 'Chiudi'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CommessaDetailPage;
